import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarIcon, Download, Eye, Settings } from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { WhatsAppMessage } from "@/utils/whatsappParser";

interface PdfExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (options: PdfExportOptions) => void;
  currentDateRange?: DateRange;
  messages: WhatsAppMessage[];
  chatFileName: string;
}

export interface PdfExportOptions {
  dateRange?: DateRange;
  includeImages: boolean;
  includeAudioTranscriptions: boolean;
  includeAiDescriptions: boolean;
}

export const PdfExportDialog = ({ open, onOpenChange, onExport, currentDateRange, messages, chatFileName }: PdfExportDialogProps) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(currentDateRange);
  const [includeImages, setIncludeImages] = useState(true);
  const [includeAudioTranscriptions, setIncludeAudioTranscriptions] = useState(true);
  const [includeAiDescriptions, setIncludeAiDescriptions] = useState(true);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleExport = () => {
    onExport({
      dateRange,
      includeImages,
      includeAudioTranscriptions,
      includeAiDescriptions,
    });
    onOpenChange(false);
  };

  const clearDateRange = () => {
    setDateRange(undefined);
  };

  // Filter messages for preview based on current options
  const previewMessages = useMemo(() => {
    let filtered = messages;
    
    // Apply date range filter
    if (dateRange?.from) {
      filtered = filtered.filter(msg => {
        const msgDate = new Date(msg.timestamp);
        const from = dateRange.from!;
        const to = dateRange.to || dateRange.from!;
        return msgDate >= from && msgDate <= new Date(to.getTime() + 86400000);
      });
    }
    
    // Return only first 5 messages for preview
    return filtered.slice(0, 5);
  }, [messages, dateRange]);

  const escapeHtml = (text: string) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const totalFilteredMessages = useMemo(() => {
    if (!dateRange?.from) return messages.length;
    
    return messages.filter(msg => {
      const msgDate = new Date(msg.timestamp);
      const from = dateRange.from!;
      const to = dateRange.to || dateRange.from!;
      return msgDate >= from && msgDate <= new Date(to.getTime() + 86400000);
    }).length;
  }, [messages, dateRange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>אפשרויות יצוא PDF</DialogTitle>
          <DialogDescription>
            בחר אפשרויות יצוא וצפה בתצוגה מקדימה
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="options" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="options" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              אפשרויות
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              תצוגה מקדימה
            </TabsTrigger>
          </TabsList>

          <TabsContent value="options" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-6 py-2">
              {/* Date Range Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">טווח תאריכים</Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-right font-normal",
                        !dateRange && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="ml-2 h-4 w-4" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "PPP", { locale: undefined })} -{" "}
                            {format(dateRange.to, "PPP", { locale: undefined })}
                          </>
                        ) : (
                          format(dateRange.from, "PPP", { locale: undefined })
                        )
                      ) : (
                        <span>כל התאריכים</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      initialFocus
                      numberOfMonths={2}
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                {dateRange?.from && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateRange}
                    className="w-full"
                  >
                    נקה טווח תאריכים
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  {totalFilteredMessages} הודעות ייוצאו
                </p>
              </div>

              {/* Attachment Options */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">כלול קבצים מצורפים</Label>
                
                <div className="space-y-3 pr-2">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <Checkbox
                      id="include-images"
                      checked={includeImages}
                      onCheckedChange={(checked) => setIncludeImages(checked as boolean)}
                    />
                    <Label
                      htmlFor="include-images"
                      className="text-sm font-normal cursor-pointer"
                    >
                      כלול תמונות
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 space-x-reverse">
                    <Checkbox
                      id="include-audio"
                      checked={includeAudioTranscriptions}
                      onCheckedChange={(checked) => setIncludeAudioTranscriptions(checked as boolean)}
                    />
                    <Label
                      htmlFor="include-audio"
                      className="text-sm font-normal cursor-pointer"
                    >
                      כלול תמלולי אודיו
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 space-x-reverse">
                    <Checkbox
                      id="include-ai"
                      checked={includeAiDescriptions}
                      onCheckedChange={(checked) => setIncludeAiDescriptions(checked as boolean)}
                    />
                    <Label
                      htmlFor="include-ai"
                      className="text-sm font-normal cursor-pointer"
                    >
                      כלול תיאורי AI
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4">
              <div className="bg-muted/50 p-3 rounded-lg border">
                <p className="text-sm text-muted-foreground text-center">
                  תצוגה מקדימה של 5 ההודעות הראשונות • {totalFilteredMessages} הודעות סה״כ
                </p>
              </div>

              {/* PDF Preview */}
              <div 
                className="bg-white border rounded-lg p-6 shadow-sm overflow-y-auto max-h-[450px]"
                style={{ direction: 'rtl' }}
              >
                {/* Preview Header */}
                <div className="text-center pb-4 mb-6 border-b-2 border-gray-800">
                  <h1 className="text-2xl font-bold mb-2">יצוא שיחה</h1>
                  <div className="text-base text-muted-foreground">{chatFileName || 'WhatsApp Chat'}</div>
                </div>

                {/* Preview Messages */}
                {previewMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    אין הודעות בטווח התאריכים שנבחר
                  </div>
                ) : (
                  <div className="space-y-3">
                    {previewMessages.map((msg) => {
                      const formattedTime = new Date(msg.timestamp).toLocaleTimeString('he-IL', {
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      
                      return (
                        <div 
                          key={msg.id}
                          className={cn(
                            "p-3 rounded-lg shadow-sm",
                            msg.isSent 
                              ? "bg-gradient-to-br from-green-50 to-green-100 border-r-4 border-blue-400 mr-12" 
                              : "bg-gradient-to-br from-white to-gray-50 border-r-4 border-purple-500 ml-12"
                          )}
                        >
                          <div className="font-bold text-sm mb-1 flex items-center gap-2">
                            <span>👤</span>
                            <span className={msg.isSent ? "text-green-800" : "text-purple-800"}>
                              {msg.sender}
                            </span>
                          </div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {msg.content}
                          </div>
                          <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                            <span>🕐</span>
                            {formattedTime}
                          </div>

                          {/* Preview Attachment Info */}
                          {msg.attachment && (
                            <div className="mt-2 p-2 bg-white/90 border border-gray-200 rounded">
                              {((msg.attachment.type === 'image' && includeImages) ||
                                (msg.attachment.type === 'audio' && includeAudioTranscriptions) ||
                                (msg.attachment.type !== 'image' && msg.attachment.type !== 'audio')) && (
                                <>
                                  <div className="text-xs font-bold text-blue-600 uppercase mb-1">
                                    📎 {msg.attachment.type}
                                  </div>
                                  {msg.attachment.fileName && (
                                    <div className="text-xs text-gray-600">{msg.attachment.fileName}</div>
                                  )}
                                  
                                  {msg.attachment.aiDescription && includeAiDescriptions && (
                                    <div className="mt-2 p-2 bg-gradient-to-r from-blue-50 to-purple-50 border-r-2 border-blue-400 rounded text-xs">
                                      <div className="font-bold text-blue-700 mb-1 flex items-center gap-1">
                                        <span>🤖</span>
                                        ניתוח AI
                                      </div>
                                      <div className="text-gray-700">{msg.attachment.aiDescription}</div>
                                    </div>
                                  )}
                                  
                                  {msg.attachment.transcription && includeAudioTranscriptions && (
                                    <div className="mt-2 p-2 bg-gradient-to-r from-orange-50 to-yellow-50 border-r-2 border-orange-400 rounded text-xs">
                                      <div className="font-bold text-orange-700 mb-1 flex items-center gap-1">
                                        <span>🎤</span>
                                        תמלול אודיו
                                      </div>
                                      <div className="text-gray-700">{msg.attachment.transcription}</div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {totalFilteredMessages > 5 && (
                      <div className="text-center py-4 text-sm text-muted-foreground border-t">
                        ... ועוד {totalFilteredMessages - 5} הודעות
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ביטול
          </Button>
          <Button onClick={handleExport}>
            <Download className="ml-2 h-4 w-4" />
            יצא PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
